<template>
  <transition name="float-fade">
    <div v-if="visible" class="floating-media-player" :style="positionStyle">
      <!-- 标题栏（可拖拽） -->
      <div class="player-header" @mousedown="startDrag">
        <span class="player-title">{{ title }}</span>
        <div class="player-controls">
          <i class="el-icon-close" @click="close"></i>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="player-body">
        <!-- 图片预览 -->
        <template v-if="type === 'image'">
          <div class="image-preview">
            <img :src="src" :alt="title" @click="openImageZoom" />
          </div>
        </template>

        <!-- 视频播放 -->
        <template v-else-if="type === 'video'">
          <video ref="videoEl" :src="src" controls autoplay
                 style="width: 100%; max-height: 300px; border-radius: 4px; background: #000;"></video>
        </template>

        <!-- 音频播放 -->
        <template v-else-if="type === 'audio'">
          <div class="audio-wrapper">
            <div class="audio-icon">
              <i class="el-icon-headset"></i>
            </div>
            <audio ref="audioEl" :src="src" controls autoplay style="width: 100%;"></audio>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'FloatingMediaPlayer',
  props: {
    visible: { type: Boolean, default: false },
    type: { type: String, default: 'image' }, // image | video | audio
    src: { type: String, default: '' },
    title: { type: String, default: '媒体预览' },
    startTime: { type: Number, default: 0 }
  },
  data() {
    return {
      // 拖拽相关
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      posX: null, // null = 使用默认 CSS 定位
      posY: null
    }
  },
  computed: {
    positionStyle() {
      if (this.posX !== null && this.posY !== null) {
        return { left: this.posX + 'px', top: this.posY + 'px', right: 'auto', bottom: 'auto' }
      }
      return {}
    }
  },
  watch: {
    visible(v) {
      if (!v) {
        // 关闭时暂停播放，重置位置
        this.stopPlayback()
        this.posX = null
        this.posY = null
      } else {
        // 打开时设置起始时间
        this.$nextTick(() => {
          const audioEl = this.$refs.audioEl
          if (this.type === 'audio' && audioEl && this.startTime > 0) {
            // 等 metadata 加载后再设置时间
            const setTime = () => {
              audioEl.currentTime = this.startTime
            }
            if (audioEl.readyState >= 1) {
              setTime()
            } else {
              audioEl.addEventListener('loadedmetadata', setTime, { once: true })
            }
          }
        })
      }
    }
  },
  methods: {
    close() {
      this.stopPlayback()
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    stopPlayback() {
      if (this.$refs.videoEl) this.$refs.videoEl.pause()
      if (this.$refs.audioEl) this.$refs.audioEl.pause()
    },
    startDrag(e) {
      if (e.target.closest('.player-controls')) return
      this.isDragging = true
      const rect = this.$el.getBoundingClientRect()
      this.dragStartX = e.clientX - rect.left
      this.dragStartY = e.clientY - rect.top
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      e.preventDefault()
    },
    onDrag(e) {
      if (!this.isDragging) return
      this.posX = e.clientX - this.dragStartX
      this.posY = e.clientY - this.dragStartY
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },
    openImageZoom() {
      // 动态创建全屏遮罩
      const overlay = document.createElement('div')
      overlay.className = 'image-zoom-overlay'
      overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:3000;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out;'
      
      const img = document.createElement('img')
      img.src = this.src
      img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;border-radius:4px;transition:transform 0.1s ease;cursor:default;'
      let scale = 1
      overlay.appendChild(img)
      
      const closeBtn = document.createElement('i')
      closeBtn.className = 'el-icon-close'
      closeBtn.style.cssText = 'position:absolute;top:20px;right:20px;font-size:28px;color:#fff;cursor:pointer;padding:8px;border-radius:50%;background:rgba(0,0,0,0.4);'
      overlay.appendChild(closeBtn)
      
      const remove = () => { document.body.removeChild(overlay) }
      overlay.addEventListener('click', (e) => { if (e.target === overlay) remove() })
      closeBtn.addEventListener('click', remove)
      overlay.addEventListener('wheel', (e) => {
        e.preventDefault()
        scale += (e.deltaY > 0 ? -0.1 : 0.1)
        scale = Math.max(0.2, Math.min(5, scale))
        img.style.transform = `scale(${scale})`
      })
      
      document.body.appendChild(overlay)
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  }
}
</script>

<style lang="scss" scoped>
.floating-media-player {
  position: fixed;
  left: 16px;
  bottom: 80px;
  z-index: 2000;
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
  user-select: none;
}

.player-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f5f7fa;
  cursor: move;
  border-bottom: 1px solid #ebeef5;
}

.player-title {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
}

.player-controls i {
  cursor: pointer;
  font-size: 16px;
  color: #909399;
  padding: 2px;
  border-radius: 4px;
  &:hover { color: #f56c6c; background: #fef0f0; }
}

.player-body {
  padding: 12px;
}

.image-preview {
  text-align: center;
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 6px;
    cursor: zoom-in;
  }
}

.audio-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.audio-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff, #337ecc);
  display: flex;
  align-items: center;
  justify-content: center;
  i { font-size: 24px; color: #fff; }
}

/* 过渡动画 */
.float-fade-enter-active, .float-fade-leave-active {
  transition: all 0.3s ease;
}
.float-fade-enter, .float-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
